import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getLastUrl, getUser, signIn } from "../../auth/Auth";
import { EditPassword, generateOtp } from "../../services/AuthServices";
import { testUrlValid } from "../../Constants";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imgMI from "../../assets/images/media-intelligence-icon.png";
import ReactLoading from "react-loading";

function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    password_confirmation: "",
  });
  const [showPass, setShowPass] = useState(false);
  const tempEmail = localStorage.getItem("temp_email");

  // Redirect if no email found in localStorage
  if (!tempEmail) {
    // We'll handle this in useEffect instead of here to avoid render issues
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.password || !formData.password_confirmation) {
      toast.error(t("account.RequiredFields"));
      return false;
    }

    if (formData.password !== formData.password_confirmation) {
      toast.error(t("account.PasswordsDoNotMatch"));
      return false;
    }

    // Basic password strength check
    if (formData.password.length < 8) {
      toast.error(t("account.PasswordTooShort"));
      return false;
    }

    return true;
  };

  const resetPassword = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const data = {
        email: tempEmail,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      };

      const response = await EditPassword(data);
      toast.success(t("account.PasswordChangedSuccess"));

      // Sign in with new credentials
      signIn(response);

      // Handle redirection with OTP if URL is valid
      const lastUrl = getLastUrl();
      if (testUrlValid(lastUrl)) {
        try {
          const otpResponse = await generateOtp(getUser().id);
          const otp = otpResponse.data.otp.otp;

          if (otp) {
            toast.success(t("account.OtpSentSuccess"));
            setTimeout(() => {
              window.location.replace(`${lastUrl}?otp=${otp}`);
            }, 1500);
          }
        } catch (otpError) {
          toast.error(t("account.OtpGenerationError"));
        }
      } else {
        // Redirect to home if no valid lastUrl
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      if (error.status === 403) {
        toast.error(error.data.message || t("account.PermissionDenied"));
      } else if (error.status === 400) {
        toast.error(error.data.message || t("account.InvalidRequest"));
      } else {
        toast.error(t("account.UnexpectedError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Common style for both password visibility toggles
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPass(!showPass);
  };

  // Styles for loader
  const styles = {
    loaderWrapper: {
      display: "flex",
      justifyContent: "center",
      marginTop: "10px",
    },
  };

  return (
    <section className="account-section">
      <div className="tf-container">
        <div className="row">
          <div className="wd-form-login">
            <ToastContainer position="top-right" autoClose={3000} />

            <a
              href={process.env.REACT_APP_CLIENT_SERVICE}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={imgMI} className="mb-4" alt="logo" />
            </a>

            <h4>{t("account.AccountpasswordReset")}</h4>

            <form onSubmit={resetPassword}>
              <div className="ip">
                <label>
                  {t("account.AccountPass")}
                  <span>*</span>
                </label>
                <div className="inputs-group auth-pass-inputgroup">
                  <input
                    type={showPass ? "text" : "password"}
                    className="input-form password-input"
                    placeholder={t("account.AccountPass")}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <Link
                    to="#"
                    className={`password-addon ${
                      showPass ? "icon-eye" : "icon-eye-off"
                    }`}
                    onClick={togglePasswordVisibility}
                  />
                </div>
              </div>

              <div className="ip">
                <label>
                  {t("account.AccountConfirmPass")}
                  <span>*</span>
                </label>
                <div className="inputs-group auth-pass-inputgroup">
                  <input
                    type={showPass ? "text" : "password"}
                    className="input-form password-input"
                    placeholder={t("account.AccountConfirmPass")}
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    required
                  />
                  <Link
                    to="#"
                    className={`password-addon ${
                      showPass ? "icon-eye" : "icon-eye-off"
                    }`}
                    onClick={togglePasswordVisibility}
                  />
                </div>
              </div>

              <div className="reset-button-container">
                <button type="submit" disabled={isLoading}>
                  {t("account.AccountConnexion")}
                </button>

                {isLoading && (
                  <div style={styles.loaderWrapper}>
                    <ReactLoading
                      type="spin"
                      color="red"
                      height={30}
                      width={30}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ResetPassword;
